/* cf-product-calculator-cta.js - Click Fencing, 2026-08-22
 *
 * Puts a route back to the calculators on every product page, at the TOP and at the BOTTOM.
 *
 * WHY
 * ---
 * A product page was a dead end. The only calculator links anywhere on it were the four
 * images inside the header mega-menu - invisible on mobile until the drawer is opened, and
 * not something a customer reading a panel listing will go looking for. Someone who lands on
 * "Blade Pool Fencing Panel 1200 x 2400" from Google has no way to say "I have 12 metres,
 * work out the kit" without hunting through the nav. That is the whole value of this site.
 *
 * WHAT IT DOES
 * ------------
 * Top: a slim card directly under the product title, inside the buy column, pointing at the
 * calculator for THIS product's range.
 * Bottom: a full-width band after the product section and before related products, offering
 * the calculator for THIS product's range only (Pete, 2026-08-22). Shared hardware that suits
 * three ranges offers those three; a product matching no range falls back to all four.
 *
 * PICKING THE RANGE
 * -----------------
 * From the product's own JSON (/products/<handle>.js) - tags, product_type and title. On the
 * 92 live products this resolves 75 to exactly one range; the other 16 are shared hardware
 * (D&D hinges, Magna latches, anchors, grout) which legitimately match three or four, and
 * one is the Site Inspection service which matches none. Multi-match products get every
 * matching calculator offered rather than a guess, and a no-match product still gets the
 * bottom band with all four.
 *
 * URL FORMS
 * ---------
 * Shopify serves the same product at /products/<h> and at /collections/<c>/products/<h>. The
 * first build anchored its path test to the start of the string, so every collection-scoped
 * URL - which is what Google and the Shop app hand out - loaded the script and then returned
 * before drawing anything. Measured 2026-08-22: /collections/pool-fence-parts-hardware/
 * products/gate-latch had no strip and no band; /products/gate-latch had both.
 *
 * Built with the theme's own tokens - Inter, ink #2B2C2D, #1C2228 buttons, 7px radius - so
 * it reads as part of the page and not as an injected widget.
 */
(function () {
  'use strict';
  if (window.__cfProductCalcCtaInstalled) return;
  window.__cfProductCalcCtaInstalled = true;
  /* Shopify serves a product at /products/<h> AND at /collections/<c>/products/<h>.
     Google and old links use the second form, so anchoring the test to the start of the
     path silently skipped those pages.
     Pete, 2026-08-23: the RANGE pages (/collections/framelessglass and friends) had no route
     to a calculator either - the hero copy says "design your exact layout in the Glass
     Calculator" and there was nothing to click. Collection pages now get the same treatment. */
  var CF_PATH = location.pathname.split('?')[0].replace(/\/$/, '');
  var IS_PRODUCT = /\/products\//.test(CF_PATH);
  var IS_COLLECTION = !IS_PRODUCT && /^\/collections\/[^\/]+/.test(CF_PATH);
  if (!IS_PRODUCT && !IS_COLLECTION) return;

  /* Photos are real Click Fencing installs, served from the shop's own CDN path so there is no
     third-party host in the page. Shopify resizes on the width parameter. The icons are drawn
     here rather than pulled from the calculators: blade, flat top and perfmesh draw an SVG
     elevation whose content depends on the run length (and carries dimension text), and the
     glass calculator draws with CSS divs and no SVG at all - none of them survive being shrunk
     to 54px. These are the same four fences in the same flat line idiom, at icon scale. */
  var ICONS = {
    glass: '<svg class="cf-cta-card__icon" viewBox="0 0 96 56" fill="none" stroke="#fff" '
      + 'stroke-width="2" stroke-linecap="round" aria-hidden="true">'
      + '<rect x="4" y="6" width="26" height="38" rx="1"/><rect x="35" y="6" width="26" height="38" rx="1"/>'
      + '<rect x="66" y="6" width="26" height="38" rx="1"/>'
      + '<path d="M10 44v6M24 44v6M41 44v6M55 44v6M72 44v6M86 44v6"/></svg>',
    blade: '<svg class="cf-cta-card__icon" viewBox="0 0 96 56" fill="none" stroke="#fff" '
      + 'stroke-width="2" stroke-linecap="round" aria-hidden="true">'
      + '<path d="M6 4v48M90 4v48"/>'
      + '<path d="M16 8v40M23 8v40M30 8v40M37 8v40M44 8v40M51 8v40M58 8v40M65 8v40M72 8v40M79 8v40"/>'
      + '</svg>',
    flattop: '<svg class="cf-cta-card__icon" viewBox="0 0 96 56" fill="none" stroke="#fff" '
      + 'stroke-width="2" stroke-linecap="round" aria-hidden="true">'
      + '<path d="M6 4v48M90 4v48M6 10h84M6 40h84"/>'
      + '<path d="M20 6v38M32 6v38M44 6v38M56 6v38M68 6v38M80 6v38"/></svg>',
    perfmesh: '<svg class="cf-cta-card__icon" viewBox="0 0 96 56" fill="none" stroke="#fff" '
      + 'stroke-width="2" stroke-linecap="round" aria-hidden="true">'
      + '<path d="M6 4v48M90 4v48"/><rect x="14" y="10" width="68" height="32" rx="1"/>'
      + '<g fill="#fff" stroke="none">'
      + '<circle cx="24" cy="20" r="1.6"/><circle cx="36" cy="20" r="1.6"/><circle cx="48" cy="20" r="1.6"/>'
      + '<circle cx="60" cy="20" r="1.6"/><circle cx="72" cy="20" r="1.6"/>'
      + '<circle cx="30" cy="32" r="1.6"/><circle cx="42" cy="32" r="1.6"/><circle cx="54" cy="32" r="1.6"/>'
      + '<circle cx="66" cy="32" r="1.6"/></g></svg>'
  };

  var CALCS = [
    { key: 'glass',    name: 'Frameless Glass',   url: '/pages/glass-fencing',
      blurb: 'Toughened glass panels, spigots and self-closing gate hardware.',
      photo: '/cdn/shop/files/frameless-glass-pool-fence-perth-black-spigots.jpg?v=1785163715&width=900',
      match: ['frameless glass', 'glass range', 'glass'] },
    { key: 'blade',    name: 'Blade',             url: '/pages/blade-fence-calculator',
      blurb: 'Vertical blade panels in Satin Black, Monument or Satin White.',
      photo: '/cdn/shop/files/Blade_Calc.jpg?v=1779728043&width=900',
      match: ['blade'] },
    { key: 'flattop',  name: 'Flat Top Tubular',  url: '/pages/flat-top-fence-calculator',
      blurb: 'Classic tubular pool fencing, 2400mm and 3000mm panels.',
      photo: '/cdn/shop/files/Flat_top.webp?v=1772569285&width=900',
      match: ['flat top', 'flat-top', 'flattop', 'tubular'] },
    { key: 'perfmesh', name: 'PerfMesh',          url: '/pages/perfmesh-fence',
      blurb: 'Perforated aluminium screening with full pool-safe compliance.',
      photo: '/cdn/shop/files/perfblack.png?v=1778601978&width=900',
      match: ['perfmesh', 'perforated', 'perf range'] }
  ];
  function familiesFor(p) {
    var blob = [].concat(p.tags || [], [p.product_type || '', p.title || ''])
                 .join(' ').toLowerCase();
    return CALCS.filter(function (c) {
      return c.match.some(function (k) { return blob.indexOf(k) !== -1; });
    });
  }

  function css() {
    if (document.getElementById('cf-calc-cta-css')) return;
    var s = document.createElement('style');
    s.id = 'cf-calc-cta-css';
    s.textContent = [
      '.cf-cta-top{margin:1.2rem 0 1.6rem;padding:1.2rem 1.4rem;border:1px solid rgba(127,127,127,.34);',
      'border-radius:9px;font-family:Inter,sans-serif;color:inherit;display:flex;align-items:center;',
      'gap:1.4rem;flex-wrap:wrap;background:rgba(127,127,127,.07)}',
      '.cf-cta-top--dark{border-color:rgba(255,255,255,.26);background:rgba(255,255,255,.06)}',
      '.cf-cta-top__line{font-size:1.4rem;line-height:1.45;margin:0;flex:1 1 22rem}',
      '.cf-cta-top__btns{display:flex;flex-wrap:wrap;gap:.7rem}',
      '.cf-cta-btn{display:inline-block;background:#1C2228;color:#fff;text-decoration:none;',
      'border-radius:7px;padding:.95rem 1.5rem;font-size:1.3rem;letter-spacing:.02em;',
      'white-space:nowrap;transition:opacity .15s ease}',
      '.cf-cta-btn:hover{opacity:.86;color:#fff}',
      '.cf-cta-top--dark .cf-cta-btn{background:#fff;color:#1C2228}',
      '.cf-cta-top--dark .cf-cta-btn:hover{color:#1C2228}',
      '.cf-cta-btn--ghost{background:transparent;color:inherit;border:1px solid rgba(127,127,127,.42)}',
      '.cf-cta-btn--ghost:hover{color:inherit;opacity:1;background:rgba(127,127,127,.12)}',
      '.cf-cta-top--dark .cf-cta-btn--ghost{background:transparent;color:#fff;border-color:rgba(255,255,255,.42)}',
      '.cf-cta-top--dark .cf-cta-btn--ghost:hover{color:#fff;background:rgba(255,255,255,.12)}',
      '.cf-cta-band{background:#F4F4F4;border-top:1px solid rgba(43,44,45,.10);',
      'border-bottom:1px solid rgba(43,44,45,.10);padding:5rem 1.5rem;font-family:Inter,sans-serif;color:#2B2C2D}',
      '.cf-cta-band__inner{max-width:120rem;margin:0 auto}',
      '.cf-cta-band__eyebrow{font-size:1.2rem;letter-spacing:.14em;text-transform:uppercase;',
      'color:#6c6f72;margin:0 0 .8rem;text-align:center}',
      '.cf-cta-band h2{font-size:3.2rem;font-weight:600;letter-spacing:.015em;margin:0 0 1rem;text-align:center}',
      '.cf-cta-band__sub{font-size:1.6rem;line-height:1.6;max-width:70rem;margin:0 auto 3.4rem;',
      'text-align:center;color:#4a4c4e}',
      '.cf-cta-grid{display:grid;gap:1.6rem;grid-template-columns:repeat(4,1fr);justify-content:center}',
      '.cf-cta-grid--1{grid-template-columns:minmax(0,46rem)}',
      '.cf-cta-grid--2{grid-template-columns:repeat(2,minmax(0,34rem))}',
      '.cf-cta-grid--3{grid-template-columns:repeat(3,1fr)}',
      '@media (max-width:1100px){.cf-cta-grid{grid-template-columns:repeat(2,1fr)}',
      '.cf-cta-grid--1{grid-template-columns:minmax(0,46rem)}}',
      '@media (max-width:600px){.cf-cta-grid{grid-template-columns:1fr}.cf-cta-band{padding:3.6rem 1.5rem}',
      '.cf-cta-band h2{font-size:2.5rem}}',
      /* The photo is a backdrop, never the thing carrying the text. A three-stop scrim sits
         over it so the copy is legible on a bright turf shot and on a dark blade shot alike,
         and the card keeps a solid ink background underneath so it still reads if the image
         never loads. */
      '.cf-cta-card{position:relative;display:flex;flex-direction:column;justify-content:flex-end;',
      'min-height:25rem;background:#1C2228 center 45%/cover no-repeat;border-radius:12px;',
      'padding:2rem 1.9rem 1.8rem;text-decoration:none;color:#fff;overflow:hidden;',
      'transition:transform .18s ease,box-shadow .18s ease}',
      '.cf-cta-card::before{content:"";position:absolute;inset:0;background:linear-gradient(180deg,',
      'rgba(10,14,18,.40) 0%,rgba(10,14,18,.66) 46%,rgba(10,14,18,.89) 100%)}',
      '.cf-cta-card>*{position:relative}',
      '.cf-cta-card:hover{transform:translateY(-3px);box-shadow:0 12px 30px rgba(10,14,18,.28);color:#fff}',
      '.cf-cta-card__icon{width:5.6rem;height:auto;margin:0 0 auto;opacity:.95;',
      'filter:drop-shadow(0 1px 3px rgba(0,0,0,.6))}',
      '.cf-cta-card--match{box-shadow:0 0 0 2px #fff inset}',
      '.cf-cta-card--match:hover{box-shadow:0 0 0 2px #fff inset,0 12px 30px rgba(10,14,18,.28)}',
      '.cf-cta-card__pill{position:absolute;top:1.6rem;right:1.6rem;background:#fff;color:#1C2228;',
      'font-size:1rem;letter-spacing:.08em;text-transform:uppercase;padding:.45rem 1rem;border-radius:999px}',
      '.cf-cta-card__name{font-size:1.8rem;font-weight:600;margin:1.6rem 0 .6rem;line-height:1.25;',
      'text-shadow:0 1px 4px rgba(0,0,0,.5)}',
      '.cf-cta-card__blurb{font-size:1.35rem;line-height:1.55;color:rgba(255,255,255,.88);',
      'margin:0 0 1.6rem;text-shadow:0 1px 4px rgba(0,0,0,.5)}',
      '.cf-cta-card__go{font-size:1.35rem;font-weight:500;text-shadow:0 1px 4px rgba(0,0,0,.5)}',
      '.cf-cta-band__foot{text-align:center;font-size:1.3rem;color:#6c6f72;margin:2.8rem 0 0}',
      /* On a collection page the strip is not inside a buy column - it spans the page, so it
         needs its own gutter and max width to line up with the grid below it. */
      '.cf-cta-top--page{max-width:120rem;margin:2.4rem auto 0;padding:1.5rem 1.8rem}',
      '@media (max-width:1240px){.cf-cta-top--page{margin-left:1.5rem;margin-right:1.5rem}}'
    ].join('');
    document.head.appendChild(s);
  }

  /* The buy column is dark on some templates and light on others. Rather than hard-coding a
     panel colour that fights one of them, the strip inherits the text colour and tints itself
     from the nearest opaque ancestor background. */
  function isDarkBehind(el) {
    var n = el;
    while (n && n !== document.documentElement) {
      var bg = getComputedStyle(n).backgroundColor || '';
      var m = bg.match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)/);
      if (m && (m[4] === undefined || parseFloat(m[4]) > 0.5)) {
        var lum = (0.2126 * +m[1] + 0.7152 * +m[2] + 0.0722 * +m[3]) / 255;
        return lum < 0.5;
      }
      n = n.parentElement;
    }
    return false;
  }

  function topCard(fams) {
    var wrap = document.createElement('div');
    wrap.className = 'cf-cta-top';
    wrap.setAttribute('data-cf-cta', 'top');
    var one = fams.length === 1;
    var line = one
      ? 'Fencing a whole pool? The <strong>' + fams[0].name + '</strong> calculator works out '
        + 'your full kit — panels, posts, gates and brackets.'
      : 'Fencing a whole pool? Our calculators work out your full kit — panels, posts, gates '
        + 'and brackets.';
    var list = one ? fams : (fams.length ? fams : CALCS);
    var btns = list.map(function (c, i) {
      return '<a class="cf-cta-btn' + (i ? ' cf-cta-btn--ghost' : '') + '" href="' + c.url + '">'
           + (one ? 'Build my ' + c.name + ' fence' : c.name) + ' \u2192</a>';
    }).join('');
    wrap.innerHTML = '<p class="cf-cta-top__line">' + line + '</p>'
                   + '<div class="cf-cta-top__btns">' + btns + '</div>';
    return wrap;
  }

  function bottomBand(fams) {
    /* Pete, 2026-08-22: a product page links to ITS OWN calculator, not to all four. Shared
       hardware that genuinely suits three ranges offers those three. A product that matches no
       range at all - the site inspection service - still gets all four, because an empty band
       helps nobody. */
    var own = fams.length ? fams : CALCS;
    var showAll = fams.length === 0;
    var cards = own.map(function (c) {
      return '<a class="cf-cta-card" href="' + c.url + '"'
           + ' style="background-image:url(' + c.photo + ')">'
           + ICONS[c.key]
           + '<p class="cf-cta-card__name">' + c.name + ' Fencing Calculator</p>'
           + '<p class="cf-cta-card__blurb">' + c.blurb + '</p>'
           + '<span class="cf-cta-card__go">Open calculator \u2192</span></a>';
    }).join('');
    var heading = own.length === 1
      ? 'Design your ' + own[0].name + ' fence in 60 seconds'
      : 'Design your fence in 60 seconds';
    var sub = showAll
      ? 'Enter your measurements and the calculator returns the exact kit \u2014 panels, posts, '
        + 'gates and every bracket \u2014 priced and ready to order. Delivered in 24 hours and '
        + 'backed by a 10 year warranty.'
      : 'Enter your measurements and the ' + (own.length === 1 ? own[0].name + ' calculator returns'
          : 'calculator returns') + ' the exact kit \u2014 panels, posts, gates and every bracket '
        + '\u2014 priced and ready to order. Delivered in 24 hours and backed by a 10 year warranty.';
    var band = document.createElement('section');
    band.className = 'cf-cta-band';
    band.setAttribute('data-cf-cta', 'bottom');
    band.innerHTML = '<div class="cf-cta-band__inner">'
      + '<p class="cf-cta-band__eyebrow">DIY pool fence kits</p>'
      + '<h2>' + heading + '</h2>'
      + '<p class="cf-cta-band__sub">' + sub + '</p>'
      + '<div class="cf-cta-grid cf-cta-grid--' + own.length + '">' + cards + '</div>'
      + '<p class="cf-cta-band__foot">Prefer us to do it? Supply and install is available '
      + 'across the Perth metro area.</p></div>';
    return band;
  }

  function insert(fams) {
    css();
    Array.prototype.forEach.call(document.querySelectorAll('[data-cf-cta]'), function (n) {
      n.parentNode.removeChild(n);
    });

    var title = document.querySelector('.product__info-container .product__title')
             || document.querySelector('.product__info-wrapper .product__title')
             || document.querySelector('.product__title');
    if (title && title.parentNode) {
      var strip = topCard(fams);
      title.parentNode.insertBefore(strip, title.nextSibling);
      if (isDarkBehind(title)) strip.classList.add('cf-cta-top--dark');
    }

    var main = document.querySelector('[id^="shopify-section-template--"][id$="__main"]')
            || document.querySelector('.product') && document.querySelector('.product').closest('.shopify-section');
    if (main && main.parentNode) {
      main.parentNode.insertBefore(bottomBand(fams), main.nextSibling);
    }
  }

  /* Dawn re-renders the whole product-info section through the Section Rendering API when the
     customer picks a variant or a colour swatch. That replaces the markup the top strip was
     inserted into, so without this the strip vanishes the moment anyone changes colour. */
  function watch(fams) {
    if (!window.MutationObserver) return;
    var busy = false;
    new MutationObserver(function () {
      if (busy) return;
      if (document.querySelector('[data-cf-cta="top"]')) return;
      if (!document.querySelector('.product__title')) return;
      busy = true;
      setTimeout(function () {
        insert(fams);
        busy = false;
        console.log('[cf-product-calculator-cta] re-inserted after a section re-render');
      }, 150);
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---- collection (range) pages -------------------------------------------------------- */

  /* No fetch needed: the collection handle plus the page's own H1 carry the range name, and
     both are in the DOM before this runs. /collections/all and the hardware collections match
     nothing and correctly fall through to all four calculators. */
  function collectionFamilies() {
    var handle = (CF_PATH.match(/^\/collections\/([^\/]+)/) || [])[1] || '';
    var h1 = document.querySelector('.collection-hero__title, #MainContent h1');
    var blob = handle.replace(/-/g, ' ') + ' ' + handle + ' '
             + (h1 ? h1.textContent : '') + ' ' + document.title;
    return familiesFor({ tags: [], product_type: '', title: blob });
  }

  function gridSection() {
    var g = document.querySelector('[id^="shopify-section-"][id$="__product-grid"]');
    if (g) return g;
    var pg = document.getElementById('product-grid') || document.querySelector('.collection');
    return pg ? pg.closest('.shopify-section') : null;
  }

  function insertCollection(fams) {
    css();
    Array.prototype.forEach.call(document.querySelectorAll('[data-cf-cta]'), function (n) {
      n.parentNode.removeChild(n);
    });
    var grid = gridSection();
    if (!grid || !grid.parentNode) return false;

    /* Top: directly under the range hero, above the search band and the filters, because that
       is where someone who has just read "design your exact layout in the calculator" looks. */
    var banner = document.querySelector('[id^="shopify-section-"][id$="__banner"]')
              || document.querySelector('.collection-hero');
    var host = (banner && banner.parentNode === grid.parentNode) ? banner : null;
    var strip = topCard(fams);
    strip.classList.add('cf-cta-top--page');
    if (host) host.parentNode.insertBefore(strip, host.nextSibling);
    else grid.parentNode.insertBefore(strip, grid);
    if (isDarkBehind(strip.parentNode)) strip.classList.add('cf-cta-top--dark');

    grid.parentNode.insertBefore(bottomBand(fams), grid.nextSibling);
    return true;
  }

  /* Dawn re-renders the grid section in place when a filter or sort changes. The strips are
     siblings of that section rather than children of it, so they normally survive - this is
     the cheap insurance for the themes where they do not. */
  function watchCollection(fams) {
    if (!window.MutationObserver) return;
    var busy = false;
    new MutationObserver(function () {
      if (busy) return;
      if (document.querySelector('[data-cf-cta="top"]')) return;
      busy = true;
      setTimeout(function () {
        insertCollection(fams);
        busy = false;
        console.log('[cf-product-calculator-cta] collection strips re-inserted');
      }, 150);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function startCollection() {
    var fams = collectionFamilies();
    var ok = insertCollection(fams);
    watchCollection(fams);
    console.log('[cf-product-calculator-cta] collection ' + CF_PATH + ' -> '
      + (fams.length ? fams.map(function (f) { return f.key; }).join(', ') : 'no range match')
      + (ok ? ', top + bottom placed' : ', NO GRID SECTION FOUND - nothing placed'));
  }

  function start() {
    var m = location.pathname.split('?')[0].replace(/\/$/, '').match(/^.*\/products\/[^\/]+/);
    var handle = m ? m[0] : location.pathname.split('?')[0].replace(/\/$/, '');
    fetch(handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var fams = familiesFor(p);
        insert(fams);
        watch(fams);
        console.log('[cf-product-calculator-cta] ' + (p.handle || '') + ' -> '
          + (fams.length ? fams.map(function (f) { return f.key; }).join(', ') : 'no range match')
          + ', top + bottom placed');
      })
      .catch(function (e) {
        insert([]);
        watch([]);
        console.warn('[cf-product-calculator-cta] product json unavailable, showing all four', e);
      });
  }

  function boot() { if (IS_PRODUCT) start(); else startCollection(); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
